<template>
  <div class="mall-history-item">
    <div class="mall-history-item__body">
      <div class="mall-history-item__info">
        <MallTextBadge v-if="isOrder && history.canceled" :label="'주문 취소'" />
        <div class="mall-history-item__head">
          <div class="mall-history-item__title-area">
            <span class="mall-history-item__date">{{ history.date }}</span>
            <span class="mall-history-item__title">{{ history.title }}</span>
          </div>
          <MallRoundButton v-if="isOrder" :label="'주문 상세'" @click="$emit('detail-click', history)" />
        </div>

        <!-- 주문상품은 최대 2개까지 노출 -->
        <template v-if="isOrder">
          <span v-for="(item, index) in visibleItems" :key="index" class="mall-history-item__line">
            {{ item.name }} • <span class="mall-history-item__quantity">{{ item.quantity }}개</span>
          </span>
        </template>
        <span v-else class="mall-history-item__line">{{ history.description }}</span>
      </div>

      <div v-if="hasMoreItems" class="mall-history-item__more" @click="$emit('detail-click', history)">
        <span class="mall-history-item__more-text">상품 전체보기</span>
        <img src="@/assets/mall/icn_caret_down.svg" alt="" />
      </div>
      <div class="mall-history-item__divider"></div>
    </div>

    <div class="mall-history-item__amount">
      <span class="mall-history-item__amount-label">결제금액</span>
      <span
        class="mall-history-item__amount-value"
        :class="{ 'mall-history-item__amount-value--canceled': history.canceled }"
      >{{ formatPrice(history.amount) }}</span>
    </div>

    <div class="mall-history-item__buttons">
      <template v-if="isOrder">
        <!-- 구매 서류: 계산서(거래명세서) -->
        <MallButton
          :label="'구매 서류'"
          :type="'secondary'"
          :size="'md'"
          @click="$emit('document-click', history)"
        />
        <MallButton
          :label="'같은 상품 담기'"
          :type="'soft'"
          :size="'md'"
          @click="$emit('add-same-click', history)"
        />
      </template>
      <MallButton
        v-else
        :label="'진료 서류'"
        :type="'secondary'"
        :size="'md'"
        @click="$emit('document-click', history)"
      />
    </div>
  </div>
</template>

<script>
import convert from '@/utilities/convert.js'
import MallButton from '@/components/new/atoms/MallButton.vue'
import MallRoundButton from '@/components/new/atoms/MallRoundButton.vue'
import MallTextBadge from '@/components/new/atoms/MallTextBadge.vue'

var MAX_VISIBLE_ITEMS = 2

export default {
  name: 'MallHistoryItem',
  components: {
    MallButton,
    MallRoundButton,
    MallTextBadge,
  },
  props: {
    // 주문: { type: 'order', date, title, canceled(환불), amount, items: [{ name, quantity }] }
    // 진료: { type: 'treatment', date, title(병원명), description, amount }
    history: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isOrder() {
      return this.history.type === 'order'
    },
    visibleItems() {
      return (this.history.items || []).slice(0, MAX_VISIBLE_ITEMS)
    },
    // 3개 이상이면 '상품 전체보기' (주문 상세로 이동)
    hasMoreItems() {
      return this.isOrder && (this.history.items || []).length > MAX_VISIBLE_ITEMS
    },
  },
  methods: {
    formatPrice(price) {
      return convert.setComma(price) + '원'
    },
  },
}
</script>

<style scoped>
.mall-history-item {
  display: flex;
  padding: 19px 15px;
  flex-direction: column;
  gap: 16px;
  border: 1px solid #E5E8EB;
  border-radius: 16px;
  background: #FFF;
}

.mall-history-item__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.mall-history-item__info {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.mall-history-item__head {
  display: flex;
  width: 100%;
  align-items: flex-start;
  gap: 8px;
}

.mall-history-item__title-area {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
  font-family: Pretendard;
  font-weight: var(--font-weight-medium);
}

/* 13px 텍스트는 Figma 렌더링 기준(150% → 20px 반올림)으로 맞춤 */
.mall-history-item__date {
  color: #4E5968;
  font-size: var(--font-size-xs);
  line-height: 20px;
}

.mall-history-item__title {
  color: var(--color-text-main);
  font-size: var(--font-size-md);
  line-height: var(--line-height-normal);
}

.mall-history-item__line {
  width: 100%;
  color: #4E5968;
  font-family: Pretendard;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
  overflow-wrap: anywhere;
}

.mall-history-item__quantity {
  color: #8B95A1;
}

.mall-history-item__more {
  display: flex;
  height: 32px;
  padding: 0 8px;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  cursor: pointer;
}

.mall-history-item__more img {
  display: block;
}

.mall-history-item__more-text {
  color: var(--color-text-main);
  font-family: Pretendard;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
  white-space: nowrap;
}

.mall-history-item__divider {
  width: 100%;
  height: 1px;
  background: #F2F4F6;
}

.mall-history-item__amount {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-family: Pretendard;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-normal);
  white-space: nowrap;
}

.mall-history-item__amount-label {
  color: #6B7684;
}

.mall-history-item__amount-value {
  color: var(--color-text-main);
}

.mall-history-item__amount-value--canceled {
  color: #B0B8C1;
  text-decoration: line-through;
}

.mall-history-item__buttons {
  display: flex;
  gap: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/new/organisms/MallHistoryItem.vue